import { css } from "@emotion/react";
import type { Column } from "@tanstack/react-table";
import type { CSSProperties } from "react";

export const tableCSS = css`
  // fixes table row sizing issues with full height cell children
  // this enables features like hovering anywhere on a cell to display controls
  height: fit-content;
  font-size: 13px;
  font-weight: 400;
  color: #1e293b;
  font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  .theme--dark & {
    color: #e2e8f0;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: var(--global-table-header-background-color, #f8fafc);
    
    .theme--dark & {
      background-color: #0b1120;
    }

    tr {
      th {
        padding: 8px 12px;
        position: relative;
        text-align: left;
        user-select: none;
        vertical-align: middle;
        font-weight: 750 !important;
        color: #0f172a !important;
        font-size: 15px !important;
        line-height: 20px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif !important;
        border-bottom: 2px solid var(--global-border-color-default, #cbd5e1);

        .theme--dark & {
          color: #f8fafc !important;
          border-bottom-color: #334155;
        }

        span,
        div {
          font-size: 15px !important;
          font-weight: 750 !important;
          color: inherit;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif !important;
        }
        &:not(:last-of-type) {
          border-right: 1px solid var(--global-border-color-default, #f1f5f9);
          .theme--dark & {
            border-right-color: #1e293b;
          }
        }
        .sort {
          /* The sortable part of the header */
          cursor: pointer;
          display: flex;
          flex-direction: row;
          align-items: center;
          font-size: 15px !important;
          font-weight: 750 !important;
          color: inherit;
          gap: var(--global-dimension-size-50, 4px);
        }
        .sort-icon {
          margin-left: 4px;
          font-size: 13px;
          vertical-align: middle;
          display: inline-block;
          opacity: 0.9;
        }
        &:hover .resizer {
          opacity: 1;
        }
        div.resizer {
          display: inline-block;
          width: 3px;
          height: 100%;
          position: absolute;
          right: 0;
          top: 0;
          cursor: col-resize;
          z-index: 4;
          touch-action: none;
          opacity: 0;
          transition: opacity 0.15s ease;
          &:hover,
          &.isResizing {
            opacity: 1;
            background: var(--global-color-primary, #00685f);
          }
        }
        // Style action menu buttons in the header
        .button[data-size="compact"][data-childless="true"] {
          padding: 0;
          border: none;
          background-color: transparent;
        }
      }
    }
  }
  tbody:not(.is-empty) {
    tr {
      // when paired with table.height:fit-content, allows table cells and their children to fill entire row height
      height: 100%;
      transition: background-color 0.12s ease;
      border-bottom: 1px solid var(--global-table-row-border-color, #f1f5f9);
      &:not(:last-of-type) {
        & > td {
          border-bottom: 1px solid var(--global-table-row-border-color, #f1f5f9);
        }
      }
      & > td {
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 400;
        color: #1e293b;
        font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
        vertical-align: middle;

        .theme--dark & {
          color: #e2e8f0;
          border-bottom-color: #1e293b;
        }

        a,
        span,
        div,
        time {
          font-size: 13px;
        }
      }
      &:hover {
        background-color: rgba(0, 104, 95, 0.04) !important;
        .theme--dark & {
          background-color: rgba(20, 186, 182, 0.08) !important;
        }
      }
      &[data-selected="true"] {
        background-color: rgba(0, 104, 95, 0.08) !important;
        .theme--dark & {
          background-color: rgba(20, 186, 182, 0.15) !important;
        }
      }
    }
  }
`;

export const borderedTableCSS = css`
  tbody:not(.is-empty) {
    tr {
      & > td {
        border-bottom: 1px solid var(--global-table-bordered-cell-border-color);
      }
      & > td:not(:last-of-type) {
        border-right: 1px solid var(--global-table-bordered-cell-border-color);
      }
    }
  }
`;

export const interactiveTableCSS = css`
  tbody:not(.is-empty) {
    tr {
      &:hover {
        background-color: var(--hover-background);
      }
    }
  }
`;

export const selectableTableCSS = css(
  tableCSS,
  interactiveTableCSS,
  css`
    tbody:not(.is-empty) {
      tr {
        cursor: pointer;
      }
    }
  `
);

export const paginationCSS = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: var(--global-table-pagination-padding);
  gap: var(--global-table-pagination-gap);
  border-top: 1px solid var(--global-table-pagination-border-color);
`;

//These are the important styles to make sticky column pinning work!
//Apply styles like this using your CSS strategy of choice with this kind of logic to head cells, data cells, footer cells, etc.
//View the index.css file for more needed styles such as border-collapse: separate
export function getCommonPinningStyles<Row>(
  column: Column<Row>
): CSSProperties {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    borderRight: isLastLeftPinnedColumn
      ? "1px solid var(--global-border-color-default)"
      : undefined,
    borderLeft: isFirstRightPinnedColumn
      ? "1px solid var(--global-border-color-default)"
      : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: 1,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
    backgroundColor: isPinned
      ? "var(--global-table-pinned-column-background-color)"
      : undefined,
  };
}
